import { ComponentType, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import GlobalBrandStyles from "./components/GlobalBrandStyles";

function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (error) {
      if (typeof window !== "undefined") {
        const reloadKey = "vite_preload_reload_ts";
        const lastReload = Number(sessionStorage.getItem(reloadKey) || "0");
        const now = Date.now();
        if (now - lastReload > 15000) {
          sessionStorage.setItem(reloadKey, String(now));
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw error;
    }
  });
}

const Home = lazyWithRetry(() => import("./pages/Home"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const SharedDashboard = lazyWithRetry(() => import("./pages/SharedDashboard"));
const PropertyManagement = lazyWithRetry(() => import("./pages/PropertyManagement"));
const StandaloneProposalReview = lazyWithRetry(() => import("./pages/StandaloneProposalReview"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="animate-spin h-8 w-8 border-[3px] border-slate-200 dark:border-slate-800 border-t-blue-600 rounded-full" />
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path={"/"} component={Home} />
        <Route path={"/budget/cross-checks"} component={Home} />
        <Route path={"/budget/:subView"} component={Home} />
        <Route path={"/budget"} component={Home} />
        <Route path={"/login"} component={Login} />
        <Route path={"/properties"} component={PropertyManagement} />
        <Route path={"/share/:token"} component={SharedDashboard} />
        <Route path={"/budget/review/:token"} component={StandaloneProposalReview} />
        <Route path={"/404"} component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <GlobalBrandStyles />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
